import { supabase } from './SupabaseConfig';
import { ShiftRepository, Shift } from '../../domain/pos/ports/ShiftRepository';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabaseShiftRepository implements ShiftRepository {
  async openShift(userId: string, shiftData: Partial<Shift>): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('shifts')
        .insert({
          owner_id: userId,
          staff_id: shiftData.staffId,
          staff_name: shiftData.staffName,
          start_time: new Date().toISOString(),
          initial_cash: shiftData.initialCash,
          expected_cash: shiftData.initialCash,
          status: 'open',
          location_id: shiftData.locationId
        })
        .select('id')
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      throw new InfrastructureError('Lỗi mở ca (Supabase)', 'Supabase', error as Error);
    }
  }

  async closeShift(userId: string, shiftId: string, updates: Partial<Shift>): Promise<void> {
    try {
      const { error } = await supabase
        .from('shifts')
        .update({
          end_time: new Date().toISOString(),
          actual_cash_counted: updates.actualCashCounted,
          discrepancy: updates.discrepancy,
          status: 'closed',
          note: updates.note,
          denominations: updates.denominations,
          total_sales: updates.totalSales,
          updated_at: new Date().toISOString()
        })
        .eq('id', shiftId)
        .eq('owner_id', userId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi chốt ca (Supabase)', 'Supabase', error as Error);
    }
  }

  async updateShiftSales(userId: string, shiftId: string, amount: number, paymentMethod: string): Promise<void> {
    try {
      // Use RPC for atomic increment or fetch and update
      // For simplicity here, fetch and update (though RPC is safer for concurrency)
      const { data: shift, error: fetchError } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', shiftId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!shift) return; // Silent return or throw descriptive

      const updates: any = {
        total_sales: (shift.total_sales || 0) + amount,
        updated_at: new Date().toISOString()
      };

      if (paymentMethod === 'cash') {
        updates.cash_sales = (shift.cash_sales || 0) + amount;
        updates.expected_cash = (shift.expected_cash || 0) + amount;
      } else if (paymentMethod === 'transfer') {
        updates.transfer_sales = (shift.transfer_sales || 0) + amount;
      }

      const { error } = await supabase
        .from('shifts')
        .update(updates)
        .eq('id', shiftId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật doanh thu ca (Supabase)', 'Supabase', error as Error);
    }
  }

  async updateShiftCashFlow(userId: string, shiftId: string, amount: number, type: 'IN' | 'OUT'): Promise<void> {
    try {
      const { data: shift, error: fetchError } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', shiftId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!shift) return;

      const updates: any = {
        updated_at: new Date().toISOString()
      };

      if (type === 'IN') {
        updates.cash_in = (shift.cash_in || 0) + amount;
        updates.expected_cash = (shift.expected_cash || 0) + amount;
      } else {
        updates.cash_out = (shift.cash_out || 0) + amount;
        updates.expected_cash = (shift.expected_cash || 0) - amount;
      }

      const { error } = await supabase
        .from('shifts')
        .update(updates)
        .eq('id', shiftId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật dòng tiền ca (Supabase)', 'Supabase', error as Error);
    }
  }

  async addExpense(userId: string, shiftId: string, expense: any): Promise<void> {
    try {
      const { data: shift, error: fetchError } = await supabase
        .from('shifts')
        .select('*')
        .eq('id', shiftId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!shift) return;

      const newExpense = {
        ...expense,
        id: `exp_${Date.now()}`,
        timestamp: new Date().toISOString()
      };

      const expenses = [...(shift.expenses || []), newExpense];

      const { error } = await supabase
        .from('shifts')
        .update({
          expenses,
          cash_out: (shift.cash_out || 0) + expense.amount,
          expected_cash: (shift.expected_cash || 0) - expense.amount,
          updated_at: new Date().toISOString()
        })
        .eq('id', shiftId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi thêm chi phí vào ca (Supabase)', 'Supabase', error as Error);
    }
  }

  async getOpenShift(userId: string, locationId?: string | null): Promise<Shift | null> {
    try {
      let query = supabase
        .from('shifts')
        .select('*')
        .eq('owner_id', userId)
        .eq('status', 'open');

      if (locationId) {
        query = query.eq('location_id', locationId);
      }

      const { data, error } = await query.order('start_time', { ascending: false }).limit(1).maybeSingle();
      if (error) throw error;
      return data as any;
    } catch (error) {
      throw new InfrastructureError('Lỗi lấy ca đang mở (Supabase)', 'Supabase', error as Error);
    }
  }

  listenOpenShift(userId: string, callback: (shift: Shift | null) => void, locationId?: string | null): () => void {
    const channel = supabase
      .channel(`open-shift-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'shifts',
        filter: `owner_id=eq.${userId}`
      }, async () => {
        const shift = await this.getOpenShift(userId, locationId);
        callback(shift);
      })
      .subscribe();

    // Initial fetch
    this.getOpenShift(userId, locationId).then(callback);

    return () => {
      supabase.removeChannel(channel);
    };
  }

  listenShifts(userId: string, callback: (shifts: Shift[]) => void, locationId?: string | null): () => void {
    const channel = supabase
      .channel(`shifts-owner-${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'shifts',
        filter: `owner_id=eq.${userId}`
      }, async () => {
        let query = supabase
          .from('shifts')
          .select('*')
          .eq('owner_id', userId);

        if (locationId) {
          query = query.eq('location_id', locationId);
        }

        const { data } = await query.order('start_time', { ascending: false });
        callback(data as any || []);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }

  async setSelfieStatus(userId: string, shiftId: string, hasSelfie: boolean): Promise<void> {
    try {
      const { error } = await supabase
        .from('shifts')
        .update({ selfie_confirmed: hasSelfie, updated_at: new Date().toISOString() })
        .eq('id', shiftId)
        .eq('owner_id', userId);

      if (error) throw error;
    } catch (error) {
      throw new InfrastructureError('Lỗi cập nhật trạng thái selfie (Supabase)', 'Supabase', error as Error);
    }
  }
}
